import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

// A Turnstile token is single-use: every submission (success OR failure) must reset the
// widget, otherwise the second attempt fails with "human verification failed" until the
// page is reloaded. Only ONE widget may be mounted at a time per screen.
const TurnstileWidget = forwardRef(function TurnstileWidget({ onToken, onInvalid, action = "signup", enabled = true }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      onInvalid?.();
      if (widgetIdRef.current && window.turnstile) window.turnstile.reset(widgetIdRef.current);
    },
  }), [onInvalid]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer = null;

    const render = () => {
      if (cancelled || !containerRef.current || !window.turnstile || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
        action,
        theme: "dark",
        "refresh-expired": "manual",
        callback: onToken,
        "expired-callback": () => {
          onInvalid();
          if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current);
        },
        "error-callback": () => onInvalid(),
        "timeout-callback": () => onInvalid(),
      });
    };

    if (window.turnstile) render();
    else {
      timer = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(timer);
          render();
        }
      }, 50);
    }
    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
      if (widgetIdRef.current) window.turnstile?.remove(widgetIdRef.current);
      widgetIdRef.current = null;
      if (containerRef.current) containerRef.current.replaceChildren();
    };
  }, [onToken, onInvalid, action, enabled]);

  if (!enabled) return null;
  return <div ref={containerRef} data-testid="turnstile-widget" aria-label="Human verification" className="my-4" />;
});

export default TurnstileWidget;
